import api from '../../../services/api';

const BASE = '/mini-photoshop';

/**
 * Build a FormData payload from an image source and optional parameters.
 *
 * @param {File|Blob|string} imageSource – a File/Blob to upload, or a URL string.
 * @param {Object} [params={}] – additional form fields. Complex objects (regions,
 *   path, adjustments, curves) are JSON-stringified automatically.
 * @returns {FormData}
 */
function _buildFormData(imageSource, params = {}) {
  const formData = new FormData();

  if (imageSource instanceof Blob) {
    formData.append('file', imageSource);
  } else if (typeof imageSource === 'string') {
    formData.append('image_url', imageSource);
  }

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value);
    }
  }

  return formData;
}

/**
 * POST helper that returns a blob (image/png).
 */
function _postBlob(url, formData) {
  return api.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    responseType: 'blob',
  });
}

/**
 * POST helper that returns JSON.
 */
function _postJSON(url, formData) {
  return api.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export const imageEditorAPI = {
  backgroundRemove: (imageSource) =>
    _postBlob(`${BASE}/background-remove`, _buildFormData(imageSource)),

  crop: (imageSource, { x, y, width, height }) =>
    _postBlob(`${BASE}/crop`, _buildFormData(imageSource, { x, y, width, height })),

  resize: (imageSource, { width, height }) =>
    _postBlob(`${BASE}/resize`, _buildFormData(imageSource, { width, height })),

  changeQuality: (imageSource, { quality }) =>
    _postBlob(`${BASE}/quality`, _buildFormData(imageSource, { quality })),

  flip: (imageSource, { direction }) =>
    _postBlob(`${BASE}/flip`, _buildFormData(imageSource, { direction })),

  brightnessContrast: (imageSource, { brightness, contrast }) =>
    _postBlob(`${BASE}/brightness-contrast`, _buildFormData(imageSource, { brightness, contrast })),

  sharpen: (imageSource, { amount, radius, threshold }) =>
    _postBlob(`${BASE}/sharpen`, _buildFormData(imageSource, { amount, radius, threshold })),

  vignette: (imageSource, { intensity, radius }) =>
    _postBlob(`${BASE}/vignette`, _buildFormData(imageSource, { intensity, radius })),

  redEye: (imageSource, { regions, threshold }) =>
    _postBlob(`${BASE}/red-eye`, _buildFormData(imageSource, { regions, threshold })),

  blemishRemove: (imageSource, { center_x, center_y, radius }) =>
    _postBlob(`${BASE}/blemish-remove`, _buildFormData(imageSource, { center_x, center_y, radius })),

  penToolClip: (imageSource, { path }) =>
    _postBlob(`${BASE}/pen-tool/clip`, _buildFormData(imageSource, { path })),

  skinEnhance: (imageSource, { smoothing, use_gfpgan }) =>
    _postBlob(`${BASE}/skin-enhance`, _buildFormData(imageSource, { smoothing, use_gfpgan })),

  skinEnhanceVariants: (imageSource) =>
    _postJSON(`${BASE}/skin-enhance/variants`, _buildFormData(imageSource)),

  colorBalance: (imageSource, { adjustments }) =>
    _postBlob(`${BASE}/color-balance`, _buildFormData(imageSource, { adjustments })),

  curves: (imageSource, { curves }) =>
    _postBlob(`${BASE}/curves`, _buildFormData(imageSource, { curves })),

  save: (imageSource, { filename }) =>
    _postJSON(`${BASE}/save`, _buildFormData(imageSource, { filename })),

  download: (imageSource) =>
    _postBlob(`${BASE}/download`, _buildFormData(imageSource)),
};
